import React, { useState, useEffect } from 'react';
import { Plus, Phone, Mail, TrendingUp, Pencil, Trash2, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const POSITIONS = ['GK', 'LB', 'LCB', 'CB', 'RCB', 'RB', 'DM', 'LCM', 'CM', 'RCM', 'AM', 'LW', 'RW', 'CF'];

const ClientTrackers = () => {
  const [clients, setClients] = useState([]);
  const [positionFilter, setPositionFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'prospecting',
    interest_level: 'medium',
    contact_email: '',
    contact_phone: '',
    position: '',
    current_club: '',
    notes: '',
    last_contact: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/client-trackers');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      status: 'prospecting',
      interest_level: 'medium',
      contact_email: '',
      contact_phone: '',
      position: '',
      current_club: '',
      notes: '',
      last_contact: ''
    });
    setEditingClient(null);
    setShowForm(false);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      status: client.status || 'prospecting',
      interest_level: client.interest_level || 'medium',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      position: client.position || '',
      current_club: client.current_club || '',
      notes: client.notes || '',
      last_contact: client.last_contact || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.put(`/client-trackers/${editingClient.id}`, formData);
        toast.success('Client updated');
      } else {
        await api.post('/client-trackers', formData);
        toast.success('Client added');
      }
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to add client');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api.delete(`/client-trackers/${id}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      prospecting: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      contacted: 'bg-white/20 text-white border-white/10',
      meeting_scheduled: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      negotiating: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      signed: 'bg-green-500/20 text-green-400 border-green-500/30',
      lost: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors.prospecting;
  };

  const getInterestColor = (level) => {
    return level === 'high' ? 'text-green-400' : level === 'medium' ? 'text-white' : 'text-zinc-400';
  };

  const filteredClients = positionFilter
    ? clients.filter(c => c.position === positionFilter)
    : clients;

  const statusGroups = {
    prospecting: filteredClients.filter(c => c.status === 'prospecting'),
    active: filteredClients.filter(c => ['contacted', 'meeting_scheduled', 'negotiating'].includes(c.status)),
    closed: filteredClients.filter(c => ['signed', 'lost'].includes(c.status))
  };

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">Player Tracker</h1>
              <p className="text-lg text-zinc-300">{filteredClients.length} potential clients{positionFilter && ` (${positionFilter})`}</p>
            </div>
            <button 
              onClick={() => { resetForm(); setShowForm(true); }} 
              data-testid="add-client-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3"
            >
              <Plus className="w-5 h-5" />
              Add Client
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Position Filter */}
        <div className="glass-card p-4 mb-6" data-testid="position-filter-bar">
          <div className="flex items-center gap-3 mb-3">
            <Search className="w-4 h-4 text-zinc-400" />
            <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Filter by Position</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setPositionFilter('')}
              data-testid="position-filter-all"
              className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide transition-all ${
                !positionFilter ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              All
            </button>
            {POSITIONS.map(pos => (
              <button
                key={pos}
                onClick={() => setPositionFilter(positionFilter === pos ? '' : pos)}
                data-testid={`position-filter-${pos}`}
                className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide transition-all ${
                  positionFilter === pos ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>
        {showForm && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold uppercase text-white">
                {editingClient ? 'Edit Client' : 'New Client'}
              </h2>
              <button 
                onClick={resetForm}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Client Name *
                  </label>
                  <input 
                    type="text" 
                    required 
                    data-testid="client-name-input"
                    placeholder="Enter client name" 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Position
                  </label>
                  <select 
                    value={formData.position} 
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                  >
                    <option value="">Select position</option>
                    <option value="GK">GK</option>
                    <option value="LB">LB</option>
                    <option value="RB">RB</option>
                    <option value="CB">CB</option>
                    <option value="DM">DM</option>
                    <option value="CM">CM</option>
                    <option value="AM">AM</option>
                    <option value="RW">RW</option>
                    <option value="LW">LW</option>
                    <option value="CF">CF</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Current Club
                  </label>
                  <input 
                    type="text" 
                    placeholder="Enter current club" 
                    value={formData.current_club} 
                    onChange={(e) => setFormData({ ...formData, current_club: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Status
                  </label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                  >
                    <option value="prospecting">Prospecting</option>
                    <option value="contacted">Contacted</option>
                    <option value="meeting_scheduled">Meeting Scheduled</option>
                    <option value="negotiating">Negotiating</option>
                    <option value="signed">Signed</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Interest Level
                  </label>
                  <select 
                    value={formData.interest_level} 
                    onChange={(e) => setFormData({ ...formData, interest_level: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                  >
                    <option value="low">Low Interest</option>
                    <option value="medium">Medium Interest</option>
                    <option value="high">High Interest</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Last Contact
                  </label>
                  <input 
                    type="date" 
                    value={formData.last_contact} 
                    onChange={(e) => setFormData({ ...formData, last_contact: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Email
                  </label>
                  <input 
                    type="email" 
                    placeholder="Enter email" 
                    value={formData.contact_email} 
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Phone
                  </label>
                  <input 
                    type="tel" 
                    placeholder="Enter phone" 
                    value={formData.contact_phone} 
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Notes
                  </label>
                  <textarea 
                    rows="3" 
                    placeholder="Add notes about this client" 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" 
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button 
                  type="submit" 
                  data-testid="save-client-button"
                  className="bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-2 rounded-sm"
                >
                  {editingClient ? 'Update Client' : 'Save Client'}
                </button>
                <button 
                  type="button" 
                  onClick={resetForm} 
                  className="bg-white/10 text-white hover:bg-white/20 px-6 py-2 rounded-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries({ prospecting: 'Prospecting', active: 'Active', closed: 'Closed' }).map(([group, label]) => (
            <div key={group}>
              <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">
                {label} ({statusGroups[group].length})
              </h3>
              <div className="space-y-3">
                {statusGroups[group].length === 0 ? (
                  <div className="glass-card p-4 text-center">
                    <p className="text-sm text-zinc-500">No clients in this stage</p>
                  </div>
                ) : (
                  statusGroups[group].map(client => (
                    <div 
                      key={client.id} 
                      className="glass-card p-4 card-hover"
                      data-testid={`client-card-${client.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{client.name}</h4>
                          <p className="text-sm text-zinc-400">
                            {client.position} {client.current_club && `• ${client.current_club}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className={`w-4 h-4 ${getInterestColor(client.interest_level)}`} />
                          <button 
                            onClick={() => handleEdit(client)}
                            data-testid={`edit-client-${client.id}`}
                            className="p-1.5 hover:bg-white/10 rounded transition-colors"
                            title="Edit client"
                          >
                            <Pencil className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase border ${getStatusColor(client.status)}`}>
                          {client.status.replace('_', ' ')}
                        </span>
                        <span className={`text-xs ${getInterestColor(client.interest_level)}`}>
                          {client.interest_level} interest
                        </span>
                      </div>

                      {client.last_contact && (
                        <p className="text-xs text-zinc-500 mb-2">
                          Last contact: {new Date(client.last_contact).toLocaleDateString()}
                        </p>
                      )}

                      {client.notes && (
                        <p className="text-xs text-zinc-400 mb-3 line-clamp-2">{client.notes}</p>
                      )}

                      {(client.contact_email || client.contact_phone) && (
                        <div className="pt-3 border-t border-white/5 space-y-1">
                          {client.contact_email && (
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <Mail className="w-3 h-3" />
                              <span>{client.contact_email}</span>
                            </div>
                          )}
                          {client.contact_phone && (
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <Phone className="w-3 h-3" />
                              <span>{client.contact_phone}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t border-white/5 flex justify-end">
                        <button 
                          onClick={() => handleDelete(client.id)} 
                          data-testid={`delete-client-${client.id}`}
                          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientTrackers;
